import React, { useState, useEffect, useCallback } from 'react';
import { adminApi, accessApi } from '../services/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const StatCard = ({ label, value, icon, color }) => (
  <div className="stat-card">
    <div className="stat-icon" style={{ background: color + '15' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
        dangerouslySetInnerHTML={{ __html: icon }} />
    </div>
    <div className="stat-label">{label}</div>
    <div className="stat-val">{value}</div>
  </div>
);

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localFolderAccess, setLocalFolderAccess] = useState({});
  const [localFileAccess, setLocalFileAccess] = useState({});
  const [activeMatrixTab, setActiveMatrixTab] = useState('folders'); // 'folders' or 'files'
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminApi.testOneDrive();
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.error || 'Request failed', steps: [] });
    } finally {
      setTesting(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [sRes, mRes] = await Promise.all([adminApi.stats(), accessApi.matrix()]);
      setStats(sRes.data);
      setMatrix(mRes.data);

      const folderAcc = {};
      const fileAcc = {};

      mRes.data.customAccessList.forEach(a => {
        if (a.folderId) folderAcc[`${a.userId}:${a.folderId}`] = true;
        if (a.fileId) fileAcc[`${a.userId}:${a.fileId}`] = true;
      });

      setLocalFolderAccess(folderAcc);
      setLocalFileAccess(fileAcc);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load admin dashboard data');
    } finally {
      setLoadingMatrix(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleFolderAccess = (userId, folderId, isRoleBased) => {
    if (isRoleBased) return;
    const key = `${userId}:${folderId}`;
    setLocalFolderAccess(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFileAccess = (userId, fileId, isRoleBased) => {
    if (isRoleBased) return;
    const key = `${userId}:${fileId}`;
    setLocalFileAccess(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const saveMatrix = async () => {
    setSaving(true);
    try {
      const folderPromises = [];
      const filePromises = [];

      // Determine changes for folders
      matrix.folders.forEach(f => {
        matrix.users.forEach(u => {
          if (['ADMIN', 'MANAGER'].includes(u.role)) return;
          const key = `${u.id}:${f.id}`;
          const wasGranted = matrix.customAccessList.some(ca => ca.userId === u.id && ca.folderId === f.id);
          const isGrantedNow = !!localFolderAccess[key];
          if (wasGranted !== isGrantedNow) {
            folderPromises.push(
              accessApi.toggleCustom({ userId: u.id, folderId: f.id, hasAccess: isGrantedNow })
            );
          }
        });
      });

      // Determine changes for files
      matrix.files.forEach(file => {
        matrix.users.forEach(u => {
          if (['ADMIN', 'MANAGER'].includes(u.role)) return;
          const key = `${u.id}:${file.id}`;
          const wasGranted = matrix.customAccessList.some(ca => ca.userId === u.id && ca.fileId === file.id);
          const isGrantedNow = !!localFileAccess[key];
          if (wasGranted !== isGrantedNow) {
            filePromises.push(
              accessApi.toggleCustom({ userId: u.id, fileId: file.id, hasAccess: isGrantedNow })
            );
          }
        });
      });

      await Promise.all([...folderPromises, ...filePromises]);
      toast.success('Permissions updated successfully!');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save permission updates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <div style={{ paddingBottom: 16 }}>
          <div className="page-title">Admin Dashboard</div>
          <div className="page-subtitle">Define user permissions at folder or file level, and audit system status</div>
        </div>
      </div>

      <div className="page-body">
        {stats && (
          <div className="stats-grid">
            <StatCard label="Total users" value={stats.stats.totalUsers} color="#F97316" icon='<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>' />
            <StatCard label="Reports" value={stats.stats.totalReports} color="#059669" icon='<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>' />
            <StatCard label="Total views" value={stats.stats.totalViews} color="#7C3AED" icon='<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>' />
            <StatCard label="Downloads" value={stats.stats.totalDownloads} color="#1D4ED8" icon='<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>' />
          </div>
        )}

        {/* OneDrive Connection Test */}
        <div className="card card-pad" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-display)' }}>OneDrive Connection Test</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                Verify SharePoint login credentials and OneDrive file indexing sync.
              </div>
            </div>
            <button className="btn btn-secondary" onClick={handleTest} disabled={testing} style={{ gap: 5 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>
              {testing ? 'Testing…' : 'Run connection test'}
            </button>
          </div>
          {testResult && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '10px 14px', borderRadius: 8, background: testResult.ok ? 'var(--success-bg)' : 'var(--danger-bg)', color: testResult.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 700, fontSize: 13 }}>
                {testResult.ok ? '✅ All checks passed — OneDrive is connected' : `❌ ${testResult.error || 'Connection failed'}`}
              </div>
              {testResult.steps?.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ flexShrink: 0, fontSize: 16 }}>{s.ok ? '✅' : '❌'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: s.ok ? 'var(--text-primary)' : 'var(--danger)' }}>{s.step}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{s.msg}</div>
                    {s.data && (
                      <pre style={{ fontSize: 11, background: '#F8FAFC', borderRadius: 6, padding: '6px 10px', marginTop: 6, overflow: 'auto', color: 'var(--text-secondary)', maxHeight: 120 }}>
                        {JSON.stringify(s.data, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Access Matrix Card */}
        <div className="card card-pad" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'var(--font-display)' }}>Access Governance Matrix</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                Configure user access rules. <span style={{ color: 'var(--accent)' }}>Blue checkmarks</span> represent default administrator/manager roles.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ display: 'flex', background: 'rgba(0, 0, 0, 0.05)', borderRadius: '8px', padding: '3px' }}>
                <button
                  className={`btn ${activeMatrixTab === 'folders' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '5px 12px', fontSize: '12.5px', borderRadius: '6px', border: 'none', background: activeMatrixTab === 'folders' ? 'var(--accent)' : 'transparent', color: activeMatrixTab === 'folders' ? 'white' : 'var(--text-primary)' }}
                  onClick={() => setActiveMatrixTab('folders')}
                >
                  📁 Folders Matrix
                </button>
                <button
                  className={`btn ${activeMatrixTab === 'files' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '5px 12px', fontSize: '12.5px', borderRadius: '6px', border: 'none', background: activeMatrixTab === 'files' ? 'var(--accent)' : 'transparent', color: activeMatrixTab === 'files' ? 'white' : 'var(--text-primary)' }}
                  onClick={() => setActiveMatrixTab('files')}
                >
                  📄 Files Matrix
                </button>
              </div>
              <button className="btn btn-primary" onClick={saveMatrix} disabled={saving}>
                {saving ? 'Saving…' : 'Save matrix'}
              </button>
            </div>
          </div>

          {loadingMatrix ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : matrix && (
            <div className="matrix-scroll-wrap" style={{ overflowX: 'auto' }}>
              <table className="matrix-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ minWidth: 240, textAlign: 'left', paddingBottom: 12, color: 'var(--text-primary)', fontWeight: 700 }}>
                      {activeMatrixTab === 'folders' ? 'OneDrive Folder Directory' : 'OneDrive File Catalog'}
                    </th>
                    {matrix.users.map(u => (
                      <th key={u.id} style={{ minWidth: 100, fontSize: 11, textAlign: 'center', paddingBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name.split(' ')[0]}</div>
                        <span className={`role-badge role-${u.role}`} style={{ marginTop: 4, display: 'inline-flex' }}>{u.role}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeMatrixTab === 'folders' ? (
                    matrix.folders.length === 0 ? (
                      <tr><td colSpan={matrix.users.length + 1} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No indexed OneDrive folders found. Trigger a crawl to populate directories.</td></tr>
                    ) : (
                      matrix.folders.map(folder => (
                        <tr key={folder.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '12px 10px' }}>
                            <div style={{ fontWeight: 600, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span>📁</span>
                              <span>{folder.name}</span>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, fontFamily: 'monospace' }}>
                              ID: {folder.id.slice(0, 16)}...
                            </div>
                          </td>
                          {matrix.users.map(u => {
                            const isRoleBased = ['ADMIN', 'MANAGER'].includes(u.role);
                            const key = `${u.id}:${folder.id}`;
                            const isOn = localFolderAccess[key];
                            return (
                              <td key={u.id} style={{ textAlign: 'center', padding: '12px 10px' }}>
                                <button
                                  className={`access-toggle ${isRoleBased ? 'role-auto' : isOn ? 'on' : ''}`}
                                  onClick={() => toggleFolderAccess(u.id, folder.id, isRoleBased)}
                                  title={isRoleBased ? 'Admin/Manager role access' : isOn ? 'Revoke Folder Access' : 'Grant Folder Access'}
                                >
                                  {(isOn || isRoleBased) && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )
                  ) : (
                    matrix.files.length === 0 ? (
                      <tr><td colSpan={matrix.users.length + 1} style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>No indexed M365 files found.</td></tr>
                    ) : (
                      matrix.files.map(file => (
                        <tr key={file.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '12px 10px' }}>
                            <div style={{ fontWeight: 600, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span>📄</span>
                              <span>{file.name}</span>
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                              Format: <span style={{ textTransform: 'uppercase' }}>{file.fileType}</span>
                            </div>
                          </td>
                          {matrix.users.map(u => {
                            const isRoleBased = ['ADMIN', 'MANAGER'].includes(u.role);
                            const key = `${u.id}:${file.id}`;
                            const isOn = localFileAccess[key];
                            return (
                              <td key={u.id} style={{ textAlign: 'center', padding: '12px 10px' }}>
                                <button
                                  className={`access-toggle ${isRoleBased ? 'role-auto' : isOn ? 'on' : ''}`}
                                  onClick={() => toggleFileAccess(u.id, file.id, isRoleBased)}
                                  title={isRoleBased ? 'Admin/Manager role access' : isOn ? 'Revoke File Access' : 'Grant File Access'}
                                >
                                  {(isOn || isRoleBased) && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Activity Logs */}
        <div className="card card-pad">
          <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-display)', marginBottom: 14 }}>Recent activity</div>
          {stats?.recentActivity?.length === 0 && <div className="empty-state"><p>No activity logs recorded.</p></div>}
          {stats?.recentActivity?.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Action</th>
                  <th>Detail</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentActivity.map(log => {
                  const actionLabels = {
                    GRANT_ACCESS: { label: 'Granted', cls: 'action-GRANT' },
                    REVOKE_ACCESS: { label: 'Revoked', cls: 'action-REVOKE' },
                    LOGIN: { label: 'Login', cls: 'action-LOGIN' },
                    SYNC_ONEDRIVE: { label: 'Crawl Sync', cls: 'action-OTHER' }
                  };
                  const action = actionLabels[log.action] || { label: log.action, cls: 'action-OTHER' };
                  return (
                    <tr key={log.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{log.user?.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{log.user?.email}</div>
                      </td>
                      <td><span className={`audit-action ${action.cls}`}>{action.label}</span></td>
                      <td style={{ color: 'var(--text-primary)' }}>{log.report?.name || log.details || '—'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        {log.createdAt ? formatDistanceToNow(new Date(log.createdAt), { addSuffix: true }) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
